import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number
};

export const PlanJourney = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const endGoal = useMemo(() => formContext.answers.endGoal, [formContext.answers.endGoal]);
    const targetDrinks = useMemo(() => formContext.answers.targetTotalDrinks, [formContext.answers.targetTotalDrinks]);
    const drinksBaseline = useMemo(() => formContext.answers.totalDrinksBaseline, [formContext.answers.totalDrinksBaseline]);
    const topGoal = useMemo(() => formContext.answers['top_goal'], [formContext.answers['top_goal']]);
    const concernLevelAnswer = useMemo(() => formContext.answers['concern_level'], [formContext.answers['concern_level']]);
    
    const [textBoxHeight, setTextBoxHeight] = useState(null);

    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        title: {
            width: '100%',
            fontWeight: TypographyWeights.BOLD,
            marginBottom: '16px'
        },
        subtitle: {
            marginBottom: '24px'
        },
        graph: {
            transition: 'transform 0.5s'
        },
        point: {
            opacity: 0
        },
        dataBox: {
            position: 'absolute',
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            marginLeft: '50px',
            opacity: 0,
            transition: 'transform 0.5s'
        },
        hidden: {
            opacity: 0
        },
        visible: {
            opacity: 1
        },
        invisible: {
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none'
        },
        svg: {
            position: 'absolute',
            overflow: 'hidden',
            width: '375px',
            height: '524px',
            transform: `translate(0, ${textBoxHeight}px)`
        },
        fadeIn: {
            animation: '$fadeIn 0.5s forwards'
        },
        shiftDown1: {
            opacity: 1,
            transform: 'translate(160px, 35px)'
        },
        shiftDown2: {
            opacity: 1,
            transform: 'translate(260px, 145px)'
        },
        shiftDown3: {
            opacity: 1,
            transform: 'translate(400px, 245px)'
        },
        percentage: {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '8px',
            right: '24px'
        },
        percentageText: {
            color: theme.palette.secondary.main
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes snake': {
            '0%': {
                strokeDashoffset: '120px' 
            },
            '100%': {
                strokeDashoffset: '60px' 
            },
        },
        '@keyframes snake2': {
            '0%': {
                strokeDashoffset: '316px' 
            },
            '100%': {
                strokeDashoffset: '158px' 
            },
        }
    })), [textBoxHeight]);

    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);
    const [step, setStep] = useState(formContext.screenDirection === 'right' ? 2 : 0);
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const [overallDirection, setOverallDirection] = useState(formContext.screenDirection);

    const [point1Y, setPoint1Y] = useState(null);

    const getPlanTargets = useCallback(() => {
        let plannedDrinks = drinksBaseline;

        let taperNumber = 0;

        if (drinksBaseline <= 15) {
            taperNumber = Math.ceil(drinksBaseline * 0.1);
        } else if (drinksBaseline > 15 && drinksBaseline <= 30) {
            taperNumber = Math.floor(drinksBaseline * 0.13);
        } else if (drinksBaseline > 30) {
            if (concernLevelAnswer < 3) {
                taperNumber = Math.floor(drinksBaseline * 0.18);
            } else if (concernLevelAnswer >= 3) {
                taperNumber = Math.ceil(drinksBaseline * 0.2);
            }
        }

        plannedDrinks = drinksBaseline - taperNumber;

        return plannedDrinks;

    }, [drinksBaseline]);

    const plannedDrinks = getPlanTargets();

    const steps = useMemo(() => [
        {
            title: 'This Week',
            subtitle: 'Your Sunnyside journey begins with your first set of drink targets, an introduction to your coaching team, and an overview of how to engage with us via text message or in the app.',
            translateX: -13,
            trackingName: 'this week plan 1',
            content: (
                <Box
                    className={`${classes.dataBox} ${overallDirection === 'left' ? classes.fadeIn : classes.visible}`}
                    style={{
                        transition: 'none',
                        transform: `translate(0, ${point1Y}px)` 
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: `1px solid ${theme.palette.secondary['200']}`,
                            backgroundColor: theme.palette.secondary.light,
                            borderRadius: '16px',
                            padding: '12px 24px',
                            width: '276px' 
                        }}
                    >
                        <Typography
                            variant={TypographyVariants.H1}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            {plannedDrinks}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H6}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            Weekly Total Drinks
                        </Typography>
                        <Box className={classes.percentage}>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19.6291 14.7573C19.5859 14.8006 19.5336 14.8336 19.4759 14.8539C19.4182 14.8742 19.3566 14.8811 19.2959 14.8743L11.9804 14.0615C11.9042 14.0533 11.8317 14.0237 11.7716 13.9761C11.7114 13.9285 11.6659 13.8649 11.6404 13.7925C11.6149 13.7202 11.6104 13.6421 11.6274 13.5673C11.6445 13.4924 11.6823 13.424 11.7366 13.3698L14.4132 10.6932L10.8067 7.08503L6.50444 11.3873C6.35202 11.5397 6.14531 11.6253 5.92978 11.6253C5.71425 11.6253 5.50754 11.5397 5.35511 11.3873L0.478169 6.51036C0.330107 6.35706 0.248179 6.15174 0.250031 5.93862C0.251883 5.7255 0.337366 5.52164 0.488071 5.37093C0.638775 5.22023 0.842641 5.13474 1.05576 5.13289C1.26888 5.13104 1.4742 5.21297 1.6275 5.36103L5.92978 9.66331L10.2321 5.36103C10.3845 5.20865 10.5912 5.12305 10.8067 5.12305C11.0223 5.12305 11.229 5.20865 11.3814 5.36103L15.565 9.54382L18.2408 6.86719C18.2786 6.82948 18.3235 6.79958 18.3729 6.77922C18.4222 6.75886 18.4751 6.74843 18.5285 6.74852C18.5742 6.74841 18.6196 6.75611 18.6627 6.77128C18.7355 6.79619 18.7997 6.84128 18.8479 6.90128C18.8961 6.96128 18.9263 7.03374 18.935 7.11023L19.7478 14.4256C19.7541 14.4863 19.7468 14.5476 19.7262 14.605C19.7057 14.6624 19.6725 14.7144 19.6291 14.7573Z"
                                    fill={theme.palette.secondary.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.percentageText}
                            >
                                {Math.floor((drinksBaseline - plannedDrinks) / drinksBaseline * 100)}
                                %
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )
        },
        {
            title: 'Your First 30 Days',
            subtitle: 'In month one, you\'ll go to know your coaching team, and have you the option to join a 30-day taper challenge to kickstart your progress alongside other new members.',
            translateX: -172,
            trackingName: 'first 30 days 1',
            content: (
                <Box
                    className={`${classes.dataBox} ${classes.visible}`}
                    style={{
                        transform: `translate(0, ${point1Y + 35}px)` 
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: `1px solid ${theme.palette.secondary['200']}`,
                            backgroundColor: theme.palette.secondary.light,
                            borderRadius: '16px',
                            padding: '12px 24px',
                            width: '276px' 
                        }}
                    >
                        <Typography
                            variant={TypographyVariants.H1}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            {Math.floor(drinksBaseline * 0.68)}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H6}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            Weekly Total Drinks
                        </Typography>
                        <Box className={classes.percentage}>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19.6291 14.7573C19.5859 14.8006 19.5336 14.8336 19.4759 14.8539C19.4182 14.8742 19.3566 14.8811 19.2959 14.8743L11.9804 14.0615C11.9042 14.0533 11.8317 14.0237 11.7716 13.9761C11.7114 13.9285 11.6659 13.8649 11.6404 13.7925C11.6149 13.7202 11.6104 13.6421 11.6274 13.5673C11.6445 13.4924 11.6823 13.424 11.7366 13.3698L14.4132 10.6932L10.8067 7.08503L6.50444 11.3873C6.35202 11.5397 6.14531 11.6253 5.92978 11.6253C5.71425 11.6253 5.50754 11.5397 5.35511 11.3873L0.478169 6.51036C0.330107 6.35706 0.248179 6.15174 0.250031 5.93862C0.251883 5.7255 0.337366 5.52164 0.488071 5.37093C0.638775 5.22023 0.842641 5.13474 1.05576 5.13289C1.26888 5.13104 1.4742 5.21297 1.6275 5.36103L5.92978 9.66331L10.2321 5.36103C10.3845 5.20865 10.5912 5.12305 10.8067 5.12305C11.0223 5.12305 11.229 5.20865 11.3814 5.36103L15.565 9.54382L18.2408 6.86719C18.2786 6.82948 18.3235 6.79958 18.3729 6.77922C18.4222 6.75886 18.4751 6.74843 18.5285 6.74852C18.5742 6.74841 18.6196 6.75611 18.6627 6.77128C18.7355 6.79619 18.7997 6.84128 18.8479 6.90128C18.8961 6.96128 18.9263 7.03374 18.935 7.11023L19.7478 14.4256C19.7541 14.4863 19.7468 14.5476 19.7262 14.605C19.7057 14.6624 19.6725 14.7144 19.6291 14.7573Z"
                                    fill={theme.palette.secondary.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.percentageText}
                            >
                                32%
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ),
        },
        {
            title: 'Months 2 - 12',
            subtitle: 'Planning your drink targets ahead and pausing to track each drink will become second nature, bringing awareness and intention to your choices to help you create long-term, sustained change.',
            translateX: (endGoal === 'Complete Zero' || targetDrinks === 0) ? -414 : -274,
            trackingName: 'months 2 - 12 1',
            content: (
                <Box
                    className={`${classes.dataBox} ${overallDirection === 'left' ? classes.visible : classes.fadeIn}`}
                    style={{
                        transform: `translate(0, ${point1Y + 145}px)`,
                        transition: formContext.screenDirection === 'right' ? 'none' : 'transform 0.5s' 
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: `1px solid ${theme.palette.secondary['200']}`,
                            backgroundColor: theme.palette.secondary.light,
                            borderRadius: '16px',
                            padding: '12px 24px',
                            width: '276px' 
                        }}
                    >
                        <Typography
                            variant={TypographyVariants.H1}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            {targetDrinks}
                        </Typography>
                        <Typography
                            variant={TypographyVariants.H6}
                            style={{
                                fontWeight: TypographyWeights.BOLD,
                            }}
                        >
                            Weekly Total Drinks
                        </Typography>
                        <Box className={classes.percentage}>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19.6291 14.7573C19.5859 14.8006 19.5336 14.8336 19.4759 14.8539C19.4182 14.8742 19.3566 14.8811 19.2959 14.8743L11.9804 14.0615C11.9042 14.0533 11.8317 14.0237 11.7716 13.9761C11.7114 13.9285 11.6659 13.8649 11.6404 13.7925C11.6149 13.7202 11.6104 13.6421 11.6274 13.5673C11.6445 13.4924 11.6823 13.424 11.7366 13.3698L14.4132 10.6932L10.8067 7.08503L6.50444 11.3873C6.35202 11.5397 6.14531 11.6253 5.92978 11.6253C5.71425 11.6253 5.50754 11.5397 5.35511 11.3873L0.478169 6.51036C0.330107 6.35706 0.248179 6.15174 0.250031 5.93862C0.251883 5.7255 0.337366 5.52164 0.488071 5.37093C0.638775 5.22023 0.842641 5.13474 1.05576 5.13289C1.26888 5.13104 1.4742 5.21297 1.6275 5.36103L5.92978 9.66331L10.2321 5.36103C10.3845 5.20865 10.5912 5.12305 10.8067 5.12305C11.0223 5.12305 11.229 5.20865 11.3814 5.36103L15.565 9.54382L18.2408 6.86719C18.2786 6.82948 18.3235 6.79958 18.3729 6.77922C18.4222 6.75886 18.4751 6.74843 18.5285 6.74852C18.5742 6.74841 18.6196 6.75611 18.6627 6.77128C18.7355 6.79619 18.7997 6.84128 18.8479 6.90128C18.8961 6.96128 18.9263 7.03374 18.935 7.11023L19.7478 14.4256C19.7541 14.4863 19.7468 14.5476 19.7262 14.605C19.7057 14.6624 19.6725 14.7144 19.6291 14.7573Z"
                                    fill={theme.palette.secondary.main}
                                />
                            </svg>
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.percentageText}
                            >
                                {`${Math.floor((drinksBaseline - targetDrinks) * 100 / drinksBaseline)}%`}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )
        }
    ], [overallDirection, point1Y, endGoal, targetDrinks, drinksBaseline, topGoal]);

    const [title, setTitle] = useState(steps[step]?.title || '');
    const [subtitle, setSubtitle] = useState(steps[step]?.subtitle || '');
    const [slideTitle, setSlideTitle] = useState(true);

    const onClick = () => {
        if (step === steps.length - 1) {
            formContext.goNext();
            return;
        }
        setStartTime(new Date().getTime());
        setStep(step + 1);
        setSlide(false);
        setSlideTitle(false);
        setDirection('right');
        setOverallDirection('left');
    };

    const onClickBack = () => {
        if (step === 0) {
            formContext.goBack();
            return;
        }
        setStartTime(null);
        setStep(step - 1);
        setSlide(false);
        setSlideTitle(false);
        setDirection('left');
        setOverallDirection('right');
    };

    useEffect(() => {
        if (slide) return;

        setTimeout(() => {
            setSubtitle(steps[step].subtitle);
        }, 500);
    }, [slide]);

    useEffect(() => {
        if (slideTitle) return;

        setTimeout(() => {
            setTitle(steps[step].title);
        }, 500);
    }, [slideTitle]);

    useEffect(() => {
        setSlide(true);
        setDirection(direction === 'right' ? 'left' : 'right');
    }, [subtitle]);

    useEffect(() => {
        setSlideTitle(true);
    }, [title]);

    useEffect(() => {
        if (typeof step !== 'number') return;
        formContext.fireTrackingEvents(formContext, steps[step].trackingName, cookies);
    }, [step]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;
        const url = steps[step].trackingName;

        dataDogLogger(
            'info',
            `Diagnostic screen ${url} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    const point1 = useRef(null);
    const textBox = useRef(null);

    useEffect(() => {
        if (!textBox.current) return;

        setTextBoxHeight(textBox.current.clientHeight);
    }, [textBox.current]);

    useEffect(() => {
        if (!point1.current) return;

        if (step === 0 && overallDirection === 'left') {
            setPoint1Y(point1.current.getBoundingClientRect().y - 146);
        } else if (step === 2 && overallDirection === 'right') {
            setPoint1Y(point1.current.getBoundingClientRect().y - 291);
        }
    }, [point1.current, step]);

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={onClickBack}
            goNext={onClick}
            buttonDelay={0}
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                    >
                        <Box className={classes.content}>
                            <Box
                                className={classes.invisible}
                                ref={textBox}
                            >
                                <Slide
                                    in={slideTitle}
                                    direction={direction}
                                    timeout={250}
                                    mountOnEnter
                                >
                                    <Typography
                                        variant={TypographyVariants.H3}
                                        className={classes.title}
                                    >
                                        {title}
                                    </Typography>
                                </Slide>
                                <Slide
                                    in={slide}
                                    direction={direction}
                                    timeout={250}
                                    mountOnEnter
                                >
                                    <Typography
                                        variant={TypographyVariants.H6}
                                        className={classes.subtitle}
                                    >
                                        {'Planning your drink targets ahead and pausing to track each drink will become second nature, bringing awareness and intention to your choices to help you create long-term, sustained change.'}
                                    </Typography>
                                </Slide>
                            </Box>
                            <Slide
                                in={slideTitle}
                                direction={direction}
                                timeout={250}
                                mountOnEnter
                            >
                                <Typography
                                    variant={TypographyVariants.H3}
                                    className={classes.title}
                                >
                                    {title}
                                </Typography>
                            </Slide>
                            <Slide
                                in={slide}
                                direction={direction}
                                timeout={250}
                                mountOnEnter
                            >
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.subtitle}
                                >
                                    {subtitle}
                                </Typography>
                            </Slide>
                            {steps[step].content}
                            <Box className={classes.svg}>
                                <svg
                                    className={classes.graph}
                                    style={{
                                        transform: `translate(${steps[step].translateX}px, 0)` 
                                    }}
                                    width="1442"
                                    height="617"
                                    viewBox="0 0 1442 587"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0 3.59775C0 3.59775 198.259 -29.1599 280.081 103.014C335.404 192.384 378.686 247.036 452.115 247.036C504.565 247.036 1442 247.036 1442 247.036V583C1442 585.209 1440.21 587 1438 587H4.00002C1.79088 587 0 585.209 0 583V3.59775Z"
                                        fill="url(#paint0_linear_9_2)"
                                    />
                                    <circle
                                        ref={point1}
                                        className={`${classes.point} ${step >= 0 && step < 1 && overallDirection === 'left' ? classes.fadeIn : classes.visible} ${step >= 1 && step < 2 ? classes.shiftDown1 : null} ${step >= 2 ? (endGoal === 'Complete Zero' || targetDrinks === 0) ? classes.shiftDown3 : classes.shiftDown2 : null}`}
                                        style={{
                                            transition: 'transform 0.5s' 
                                        }}
                                        cx="50"
                                        cy="0"
                                        r="16"
                                        strokeWidth={3}
                                        stroke={theme.palette.white}
                                        fill={theme.palette.secondary.main}
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_9_2"
                                            x1="416.45"
                                            y1="152.742"
                                            x2="354.959"
                                            y2="595.143"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop
                                                stopColor={theme.palette.secondary.main}
                                                stopOpacity="0.45"
                                            />
                                            <stop
                                                offset="0.527373"
                                                stopColor={theme.palette.secondary.main}
                                                stopOpacity="0.16"
                                            />
                                            <stop
                                                offset="1"
                                                stopColor={theme.palette.secondary.main}
                                                stopOpacity="0"
                                            />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </Box>
                        </Box>
                    </Slide>
                    <Box
                        style={{
                            height: '700px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default PlanJourney;
